import React, { useEffect } from 'react'

import { DropdownPosition } from './useDropdownPosition'
import ReactDOM from 'react-dom'

import { Box } from '@chakra-ui/react'

import { Spinner } from '../Spinner'
import { Tag } from '../Tag'
import {
  ColorScheme,
  checkboxOptionCaptionStyles,
  checkboxOptionRowStyles,
  checkboxOptionSubtitleStyles,
  checkboxStyles,
  dropdownItemStyles,
  dropdownStyles,
  flatDropdownStyles,
  footerRowStyles,
  noResultsItemStyles,
  primaryActionContainerStyles,
  primaryActionHeaderStyles,
  primaryActionItemStyles,
} from './TypeaheadSearch.styles'
import { DropdownItem, PrimaryAction } from './TypeaheadSearch.types'

export type TypeaheadDropdownProps = {
  dropdownItems: DropdownItem[]
  activeIndex: number
  isCheckboxReadOnly?: boolean
  colorScheme: ColorScheme
  dropdownPos: DropdownPosition
  dropdownRef: React.MutableRefObject<HTMLDivElement | null>
  maxDropdownHeight?: string | number
  primaryAction?: PrimaryAction
  primaryActionLabel: string
  primaryActionPosition: 'before' | 'after'
  resultsHeader?: string
  noMatchLabel: string
  noOptionsLabel?: string
  emptyLabel?: string
  showPrimaryAction: boolean
  setActiveIndex: (i: number) => void
  handleSelectOption: (value: string) => void
  handlePrimaryAction: () => void
  handleToggleOption: (value: string) => void
  footerContent?: React.ReactNode
  /** Content rendered at the top of the dropdown, above the results header. */
  headerContent?: React.ReactNode
  /**
   * When true (default), renders inline below the input with no portal.
   * Pass false to portal to document.body (needed inside overflow:hidden containers like TagGroup).
   */
  inline?: boolean
  /** Override the dropdown background color (e.g. to match a parent container). */
  dropdownBg?: string
  /** Override the text color of the results header ("Select label" row). */
  headerColor?: string
  /** When true, the dropdown expands to fill remaining vertical space in its flex parent. */
  fillHeight?: boolean
  /** When true, removes the dropdown's border, box-shadow, and border-radius. */
  flatDropdown?: boolean
  /** Badge text shown next to a checkbox option's label when `option.isDisabled` is true. Defaults to `'Disabled'`. */
  disabledBadgeLabel?: string
}

export const TypeaheadDropdown = ({
  dropdownItems,
  activeIndex,
  isCheckboxReadOnly = false,
  colorScheme,
  dropdownPos,
  dropdownRef,
  primaryAction,
  primaryActionLabel,
  primaryActionPosition,
  resultsHeader,
  noMatchLabel,
  noOptionsLabel,
  emptyLabel,
  showPrimaryAction,
  setActiveIndex,
  handleSelectOption,
  handlePrimaryAction,
  handleToggleOption,
  footerContent,
  headerContent,
  maxDropdownHeight,
  inline = true,
  dropdownBg,
  headerColor,
  fillHeight = false,
  flatDropdown = false,
  disabledBadgeLabel = 'Disabled',
}: TypeaheadDropdownProps) => {
  useEffect(() => {
    const activeItem = dropdownRef.current?.querySelector<HTMLElement>(
      '[data-typeahead-active="true"]'
    )
    activeItem?.scrollIntoView?.({ block: 'nearest' })
  }, [activeIndex, dropdownRef])

  const dropdownContent = (
    <Box
      {...(!inline && {
        left: `${dropdownPos.left}px`,
        position: 'absolute',
        top: `${dropdownPos.top}px`,
        transform: dropdownPos.isOpenAbove ? 'translateY(-100%)' : undefined,
        width: `${dropdownPos.width}px`,
        zIndex: 1500,
      })}
      ref={(node: HTMLDivElement | null) => {
        dropdownRef.current = node
      }}
      {...dropdownStyles(colorScheme, false)}
      {...(flatDropdown && flatDropdownStyles())}
      {...(dropdownBg !== undefined && {
        style: { backgroundColor: dropdownBg },
      })}
      {...(fillHeight
        ? { flex: 1, overflowY: 'auto' }
        : maxDropdownHeight !== undefined && {
            maxHeight:
              typeof maxDropdownHeight === 'number'
                ? `${maxDropdownHeight}px`
                : maxDropdownHeight,
            overflowY: 'auto',
          })}
    >
      {dropdownItems.map((item, i) => {
        if (item.type === 'option') {
          return (
            <Box
              key={item.option.value}
              {...dropdownItemStyles(i === activeIndex, colorScheme)}
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault()
                handleSelectOption(item.option.value)
              }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              {item.option.label ?? item.option.value}
            </Box>
          )
        }
        if (item.type === 'action') {
          return (
            <Box
              key='__action__'
              {...(i > 0 ? primaryActionContainerStyles(colorScheme) : {})}
            >
              {primaryAction?.header && (
                <Box {...primaryActionHeaderStyles(colorScheme)}>
                  {primaryAction.header}
                </Box>
              )}
              <Box
                {...primaryActionItemStyles(i === activeIndex, colorScheme)}
                onMouseDown={(e: React.MouseEvent) => {
                  e.preventDefault()
                  handlePrimaryAction()
                }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                {primaryActionLabel}
              </Box>
            </Box>
          )
        }
        if (item.type === 'results-header') {
          const hasBorderTop =
            showPrimaryAction && primaryActionPosition === 'before'
          return (
            <Box
              key='__results-header__'
              {...primaryActionHeaderStyles(colorScheme)}
              {...(hasBorderTop && primaryActionContainerStyles(colorScheme))}
              position='sticky'
              top={0}
              zIndex={1}
              {...(dropdownBg !== undefined && {
                style: { backgroundColor: dropdownBg },
              })}
              {...(headerColor !== undefined && { color: headerColor })}
            >
              {resultsHeader}
            </Box>
          )
        }
        if (item.type === 'no-match') {
          return (
            <Box key='__no-match__' {...noResultsItemStyles(colorScheme)}>
              {noMatchLabel}
            </Box>
          )
        }
        if (item.type === 'no-options') {
          return (
            <Box key='__no-options__' {...noResultsItemStyles(colorScheme)}>
              {noOptionsLabel}
            </Box>
          )
        }
        if (item.type === 'empty') {
          return (
            <Box key='__empty__' {...noResultsItemStyles(colorScheme)}>
              {emptyLabel}
            </Box>
          )
        }
        if (item.type === 'loading') {
          return (
            <Box
              alignItems='center'
              display='flex'
              justifyContent='center'
              key='__loading__'
              {...noResultsItemStyles(colorScheme)}
            >
              <Spinner colorScheme={colorScheme} size='sm' />
            </Box>
          )
        }
        if (item.type === 'checkbox-option') {
          return (
            <Box
              aria-checked={item.isIndeterminate ? 'mixed' : item.isSelected}
              aria-disabled={isCheckboxReadOnly || undefined}
              aria-readonly={isCheckboxReadOnly || undefined}
              data-typeahead-active={i === activeIndex || undefined}
              key={item.option.value}
              role='checkbox'
              {...checkboxOptionRowStyles(
                i === activeIndex,
                colorScheme,
                isCheckboxReadOnly
              )}
              onClick={
                isCheckboxReadOnly
                  ? undefined
                  : () => handleToggleOption(item.option.value)
              }
              onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
              onMouseEnter={
                isCheckboxReadOnly ? undefined : () => setActiveIndex(i)
              }
            >
              <Box
                {...checkboxStyles(
                  item.isSelected,
                  colorScheme,
                  item.isIndeterminate
                )}
              />
              <Box flex={1} minW={0} overflow='hidden'>
                <Box
                  overflow='hidden'
                  textOverflow='ellipsis'
                  whiteSpace='nowrap'
                >
                  {item.option.label ?? item.option.value}
                </Box>
                {item.option.subtitle && (
                  <Box {...checkboxOptionSubtitleStyles(colorScheme)}>
                    {item.option.subtitle}
                  </Box>
                )}
                {item.option.caption && (
                  <Box {...checkboxOptionCaptionStyles(colorScheme)}>
                    {item.option.caption}
                  </Box>
                )}
              </Box>
              {item.option.isDisabled && (
                <Tag
                  colorScheme={colorScheme}
                  disableTooltip
                  isArchived
                  value={disabledBadgeLabel}
                />
              )}
            </Box>
          )
        }
        if (item.type === 'footer') {
          return (
            <Box key='__footer__' {...footerRowStyles(colorScheme)}>
              {footerContent}
            </Box>
          )
        }
        if (item.type === 'header-content') {
          return <Box key='__header-content__'>{headerContent}</Box>
        }
        return null
      })}
    </Box>
  )

  if (inline) return dropdownContent
  return ReactDOM.createPortal(dropdownContent, document.body)
}
